import { Button, ButtonsGroup, ModuleLayout, useModal } from '@billy/design-system'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_PRODUCT } from '@features/products/constants/defaultProduct'
import { ProductAction } from '@features/products/enums/productAction'
import { useProducts } from '@features/products/hooks/useProducts'
import { useProductsListFilters } from '@features/products/hooks/useProductsListFilters'
import { Product } from '@features/products/types/product'
import { VatRatesContextProvider } from '@features/vatRates/contexts/vatRatesContext'

import { ModalId } from '../../../enums/modalId'
import { useSegment } from '../../../hooks'
import { ProductEditor } from '../elements/ProductEditor'
import { ProductsActions } from '../elements/ProductsActions'
import { ProductsImportModal } from '../elements/ProductsImportModal'
import { ProductsList } from './ProductsList'
import { ProductsTableHoverActionHandler } from './ProductsList/elements/ProductsTableHoverActions/types/productsTableHoverActionHandler'
import { ProductsListEmpty } from './ProductsListEmpty'

export const Products = () => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { isLoading, isError, products, totalItems } = useProducts()
  const [{ searchQuery }] = useProductsListFilters()
  const { close, open } = useModal(ModalId.ProductEditorModal)

  const [currentProduct, setCurrentProduct] = useState<Product>(DEFAULT_PRODUCT)
  const [currentProductAction, setCurrentProductAction] = useState<ProductAction>(ProductAction.Create)

  const shouldShowEmptyProductsPage = !isLoading && !products?.length && !isError && !searchQuery

  /**
   * We only want to show "create more" switch if the user is creating a new product from product list
   * and not when creating one from the product select
   */
  const withCreateMoreSwitch = currentProductAction === ProductAction.Create

  const handleCreateProductClick = useCallback(() => {
    track('XXX - Invoicing Core - Item lists - Create item clicked')
    setCurrentProduct(DEFAULT_PRODUCT)
    setCurrentProductAction(ProductAction.Create)
    open()
  }, [open, track])

  const handleEditProductClick = useCallback<ProductsTableHoverActionHandler>(
    (product: Product) => {
      setCurrentProduct(product)
      setCurrentProductAction(ProductAction.Update)
      open()
    },
    [open],
  )

  const handleDuplicateProductClick = useCallback<ProductsTableHoverActionHandler>(
    (product: Product) => {
      setCurrentProduct(product)
      setCurrentProductAction(ProductAction.Duplicate)
      open()
    },
    [open],
  )

  const productList = useMemo(
    () =>
      shouldShowEmptyProductsPage ? (
        <ProductsListEmpty />
      ) : (
        <VatRatesContextProvider>
          <ProductsList
            isError={isError}
            isLoading={isLoading}
            onDuplicateProductClick={handleDuplicateProductClick}
            onEditProductClick={handleEditProductClick}
            products={products}
            totalPageItems={totalItems}
          />
        </VatRatesContextProvider>
      ),
    [
      handleDuplicateProductClick,
      handleEditProductClick,
      isError,
      isLoading,
      products,
      shouldShowEmptyProductsPage,
      totalItems,
    ],
  )

  const sideActions = useMemo(
    () =>
      !shouldShowEmptyProductsPage && (
        <ButtonsGroup>
          <Button icon="circlePlus" onClick={handleCreateProductClick}>
            {t('products.create_product')}
          </Button>
          <ProductsActions />
        </ButtonsGroup>
      ),
    [handleCreateProductClick, shouldShowEmptyProductsPage, t],
  )

  return (
    <>
      <ModuleLayout title={t('products')} sideActions={sideActions}>
        {productList}
      </ModuleLayout>
      <ProductEditor
        action={currentProductAction}
        id={ModalId.ProductEditorModal}
        onClose={close}
        product={currentProduct}
        view="products"
        withCreateMoreSwitch={withCreateMoreSwitch}
      />
      <ProductsImportModal id={ModalId.ProductsImportModal} />
    </>
  )
}
